import { ComputedDataKey } from "../../constants/common.const";
import { RideComputedDataKeys } from "../../constants/rides.const";
import { IRideTableDto } from "../../dto";

/** Ride columns always selected for grid search (see BE `getRideCustomFieldsQueryObject`). */
const LIVE_MAP_POLL_RIDE_FIELDS = [
  "updatedAt",
  /** Computed per poll from trackings; ride `updatedAt` does not change. */
  "statusLate",
  "timeLate",
  "plannedArrivalTime",
  "actualArrivalTime",
  "realStatusRideStartByGps",
] as const satisfies readonly (keyof IRideTableDto)[];

/** Returns true when poll-visible row fields are unchanged between grid polls. */
export const areLiveMapPollRideRowsEqual = (
  prev: IRideTableDto,
  next: IRideTableDto,
): boolean => {
  for (const field of LIVE_MAP_POLL_RIDE_FIELDS) {
    if (prev[field] !== next[field]) {
      return false;
    }
  }

  const prevComputed = prev[ComputedDataKey];
  const nextComputed = next[ComputedDataKey];

  if (
    prevComputed?.[RideComputedDataKeys.newIncidentsCount] !==
    nextComputed?.[RideComputedDataKeys.newIncidentsCount]
  ) {
    return false;
  }

  return true;
};

export interface ILiveMapPollRidesMergeResult {
  merged: IRideTableDto[];
  changed: boolean;
}

/**
 * Merges a live-map background poll snapshot into the grid store while preserving
 * unchanged row object references (reduces selector / react-window churn).
 */
export const mergeLiveMapPollRides = (
  prev: readonly IRideTableDto[] | undefined,
  incoming: readonly IRideTableDto[],
): ILiveMapPollRidesMergeResult => {
  if (!prev?.length) {
    const result: ILiveMapPollRidesMergeResult = {
      merged: [...incoming],
      changed: incoming.length > 0,
    };
    return result;
  }

  const prevById = new Map(prev.map(row => [row.id, row]));
  let isRowChanged = prev.length !== incoming.length;
  const merged: IRideTableDto[] = [];

  for (const nextRow of incoming) {
    const prevRow = prevById.get(nextRow.id);

    if (!prevRow) {
      merged.push(nextRow);
      isRowChanged = true;
      continue;
    }

    if (areLiveMapPollRideRowsEqual(prevRow, nextRow)) {
      merged.push(prevRow);
    } else {
      merged.push(nextRow);
      isRowChanged = true;
    }
  }

  if (!isRowChanged) {
    for (let index = 0; index < merged.length; index += 1) {
      if (merged[index] !== prev[index]) {
        isRowChanged = true;
        break;
      }
    }
  }

  const result: ILiveMapPollRidesMergeResult = {
    merged,
    changed: isRowChanged,
  };
  return result;
};
